// English source dictionary — canonical text for every UI-chrome string that
// is NOT already driven by the CMS (see pages/admin/AdminCMS.jsx CMS_STRUCTURE
// for the editorial copy, which is translated via the CMS `_translations`
// mechanism instead of this file). Every key here must also exist in
// pt-BR.js and es.js; LanguageContext falls back to this file for any key
// missing from another locale.
const en = {
  common: {
    backToHome: 'Back to Home',
    studentAlt: 'Student {{index}}',
  },
  nav: {
    home: 'Home',
    courses: 'Courses',
    pricing: 'Pricing',
    about: 'About',
    contact: 'Contact',
    faq: 'FAQ',
    levelTest: 'Level Test',
    bookTrial: 'Book a Trial',
  },
  home: {
    badges: {
      globalExpertise: 'GLOBAL EXPERTISE',
      cambridgeCertified: 'CAMBRIDGE CERTIFIED',
      academicExcellence: 'ACADEMIC EXCELLENCE',
      nativeSpeakerInsight: 'NATIVE SPEAKER INSIGHT',
      resultsDriven: 'RESULTS DRIVEN',
    },
    trust: {
      businessName: 'Business English', businessLine1: 'Business', businessLine2: 'English',
      generalName: 'General English', generalLine1: 'General', generalLine2: 'English',
      speakingName: 'Speaking Development', speakingLine1: 'Speaking', speakingLine2: 'Development',
      academicName: 'Academic Writing', academicLine1: 'Academic', academicLine2: 'Writing',
      portugueseName: 'Portugues For Foreigners', portugueseLine1: 'Portugues', portugueseLine2: 'For Foreigners',
      oneToOneName: 'One-to-One Class', oneToOneLine1: 'One-to-One', oneToOneLine2: 'Class',
    },
    video: {
      playVideo: 'Play video',
      replayVideo: 'Replay video',
      replay: 'Replay',
      mute: 'Mute',
      unmute: 'Unmute',
      volume: 'Volume',
      liveLessons: 'Live lessons available now',
    },
    heroImageAlt: 'Cida, English teacher, welcoming students',
    carousel: {
      previous: 'Previous',
      next: 'Next',
      navigation: 'Carousel navigation',
      show: 'Show {{title}}',
    },
    community: {
      multiculturalStudents: 'Multicultural students',
      aviationLesson: 'English for aviation lesson',
      readingStrategiesLesson: 'Advanced reading strategies lesson',
      badge: 'Global Community',
      title: 'Join Learners From [hl]Around the World[/hl]',
      body: "Our students come from diverse backgrounds and cultures, united by the goal of confident English communication. You'll join a supportive community of learners from Latin America, Europe, Asia, and beyond.",
      stat: '500+ happy students worldwide',
    },
    learnMore: 'Learn More',
    viewAllTestimonials: 'View all testimonials',
    hero: {
      badge: 'CERTIFIED CELTA & TESOL TEACHERS.',
      brand: 'FRANKLY TALKING',
      title: 'Speak English with Confidence',
      subtitle: 'Personalised English lessons for real-life communication, international opportunities and confident speaking.',
      ctaPrimary: 'Test Your Level',
      ctaSecondary: 'Book a Trial',
    },
    why: {
      badge: 'Why Choose Us',
      title: 'Learn English with [hl]Confidence[/hl]',
      body: 'Learn English with confidence, guided by a highly qualified and internationally experienced teacher. You will be taught by a native Portuguese-speaking instructor with extensive global experience in English language teaching, fully certified by the University of Cambridge.',
      body2: "With a Master's degree and ongoing doctoral research in English Language Teaching methodology, our approach combines academic excellence with practical, real-world communication skills to help you achieve fluency faster and more effectively.",
      closing: 'Start your journey to fluent English today.',
      differentLabel: 'What makes us different:',
    },
    howItWorks: {
      badge: 'Getting Started',
      title: 'How It Works',
      subtitle: 'A simple journey from your first step to confident communication.',
    },
    programsSection: {
      badge: 'Our Courses',
      title: 'Popular Programs',
      subtitle: 'Flexible learning options designed for real goals and real progress.',
      cta: 'View All Courses',
    },
    testimonialsSection: {
      badge: 'Testimonials',
      title: 'What Our Students Say',
      subtitle: 'Real stories from our global community of learners.',
    },
    ctaSection: {
      title: 'Ready to Start Your [hl]English Journey[/hl]?',
      body: 'Take your free level test or book a trial lesson and discover the right learning path for your goals.',
      primary: 'Test Your Level',
      secondary: 'Book a Trial',
    },
    featuresList: [
      { title: 'International Teaching Experience', description: 'Experience teaching students from different countries, cultures and learning backgrounds.' },
      { title: 'Cambridge-Certified Expertise', description: 'Cambridge-certified expertise with advanced academic training in English Language Teaching.' },
      { title: "Master's Degree and PhD-Level Specialisation", description: "Academic excellence combining a Master's degree with ongoing doctoral research in English Language Teaching methodology." },
      { title: 'Clear Explanations for Portuguese Speakers', description: 'A native Portuguese-speaking teacher who understands your challenges and explains English clearly.' },
      { title: 'Speaking Development', description: 'Practical lessons designed to build confidence in real conversations.' },
    ],
    stepsList: [
      { title: 'Test Your Level', description: 'Find out your current level through a quick and practical assessment.' },
      { title: 'Get Your Recommended Plan', description: 'We suggest the package that best fits your level and learning goals.' },
      { title: 'Book and Start Learning', description: 'Choose your package, schedule your lessons, and begin learning with confidence.' },
    ],
    programsList: [
      { title: 'General English', subtitle: 'From Beginners to Advanced', description: 'Build confidence in speaking, listening, reading, writing, grammar, and pronunciation.' },
      { title: 'Business English', subtitle: 'Professional Communication', description: 'Communicate more professionally and confidently in meetings, emails, presentations, and interviews.' },
      { title: 'Academic English', subtitle: 'English for Academic Purposes', description: 'Prepare for essays, lectures, presentations, note-taking, and academic success.' },
    ],
  },
  courses: {
    personalizedFor: 'Personalized for',
    yourGoals: 'Your Goals',
    availableNow: 'Available Now',
    hero: {
      badge: 'Our Programs',
      title: 'Courses Designed Around [hl]Real Goals[/hl]',
      body: 'Whether you want to improve your English for work, study, travel, or personal growth, our courses are personalized to help you move forward with confidence.',
    },
    ctaSection: {
      title: 'Not sure which course is right for you?',
      body: 'Take the free level test or book a trial lesson and let us help you choose the best path.',
      primary: 'Test Your Level',
      secondary: 'Book a Trial',
    },
    coursesList: [
      {
        title: 'General English for Real Life',
        subtitle: 'From Beginners to Advanced',
        description: 'Speak English with confidence wherever life takes you. Our General English classes prepare you for travel, exchange programs, tourism, work opportunities, and building relationships in international and multicultural contexts. Less theory, more real communication. Get the English you actually need to live, connect, and succeed internationally.',
        features: ['Handle airports, hotels, and travel situations with ease', 'Communicate professionally at work and in interviews', 'Connect naturally on social media and in global environments', 'Build meaningful relationships with people from different cultures', 'Make friends and navigate real-life conversations abroad'],
        button: 'Book a Trial Lesson',
      },
      {
        title: 'Business English',
        subtitle: 'Professional Communication',
        description: 'Develop the language and communication skills you need for meetings, presentations, interviews, emails, negotiations, and international workplace interactions.',
        features: ['Email writing', 'Presentations', 'Negotiations', 'Interview prep'],
        button: 'Book a Trial Lesson',
      },
      {
        title: 'English for Academic Purposes',
        subtitle: 'Academic Success',
        description: 'Prepare for academic success with lessons designed around essays, lectures, note-taking, academic reading, presentations, and effective study communication in English.',
        features: ['Essay writing', 'Note-taking', 'Research skills', 'Academic vocabulary'],
        button: 'Book a Trial Lesson',
      },
      {
        title: 'Conversation Classes',
        subtitle: 'Fluency & Confidence',
        description: 'Practice real speaking with confidence through dynamic discussion-based lessons focused on fluency, pronunciation, spontaneity, and natural communication.',
        features: ['Discussion topics', 'Pronunciation', 'Natural flow', 'Confidence building'],
        button: 'Book a Trial Lesson',
      },
      {
        title: 'Brazilian Portuguese',
        subtitle: 'For Native English Speakers',
        description: 'Learn Brazilian Portuguese in a natural, personalized, and confidence-building way with a native teacher who can help you speak clearly and comfortably.',
        features: ['Native teacher', 'Cultural context', 'Practical phrases', 'Personalized pace'],
        button: 'Book a Trial Lesson',
      },
    ],
  },
  pricing: {
    mostPopular: 'MOST POPULAR',
    startingAt: 'Starting At',
    getStarted: 'Get Started',
    groupOption: 'Group option',
    groupOptionCaption: 'Shared momentum with a structured weekly routine.',
    privateOption: 'Private option',
    privateOptionCaption: 'Maximum personalisation for accelerated progress.',
    focusLabel: 'Focus',
    focusValue: 'Fast speaking development',
    rhythmLabel: 'Rhythm',
    rhythmValue: 'Consistent weekly practice',
    idealForLabel: 'Ideal for',
    idealForValue: 'Students with urgent goals',
    intensiveMainImageAlt: 'Student in an online intensive English lesson',
    intensiveSecondaryImageAlt: 'Teacher guiding a live English lesson',
    recommendedWhen: 'Recommended when',
    recommendedWhenTitle: 'You need results on a shorter timeline',
    recommendedWhenBody: 'Ideal for relocation, career goals, interviews, travel, or academic preparation.',
    specialOffersBadge: 'Special Offers',
    specialOffersTitle: 'Bonuses & Promotions',
    specialOffersSubtitle: 'Extra value for committed learners.',
    planLabels: {
      private: 'Personalised attention',
      pair: 'Shared learning',
      trio: 'Small team format',
      group: 'Best value',
    },
    hero: {
      badge: 'Pricing',
      title: 'Choose Your [hl]Plan[/hl]',
      body: 'Whether you prefer personalised attention or learning with others, we have the perfect option for you.',
    },
    ctaSection: {
      title: 'Not sure which package is right for you?',
      body: 'Take the free level test and get a recommended plan based on your level and goals.',
      primary: 'Test Your Level',
      secondary: 'Book a Trial',
    },
    intensivePlan: {
      badge: 'Want Faster Results?',
      title: 'Intensive Monthly Plan',
      subtitle: '3 classes per week',
      body: 'Perfect for students who want real progress in less time.',
      groupPriceTemplate: 'From {{amount}}/month (group)',
      privatePriceTemplate: 'From {{amount}}/month (private)',
    },
    whyStudentsBadge: 'Why Students Choose Us',
    whyStudentsList: [
      { text: 'Practical English for real-life situations abroad' },
      { text: 'Small groups mean more speaking time' },
      { text: 'Flexible schedules adapted to your routine' },
      { text: 'Premium teaching with international qualifications' },
    ],
    priceTemplate: '1 hour: {{amount}}',
    priceTemplatePerStudent: '1 hour: {{amount}} per student',
    lessonsTemplate: '10-class pack: {{amount}} (save {{save}})',
    durationTemplate: 'Intensive (1.5h): {{amount}}',
    packagesList: [
      { name: 'Private Classes (1:1)', description: 'For fast progress and full personalisation', features: ['Full personalised attention', 'Flexible schedule', 'Google Meet included', 'Progress tracking', 'Custom materials'] },
      { name: 'Pair Classes (2 students)', description: 'Learn with a friend and stay motivated', features: ['Learn with a friend', 'Lower cost per student', 'Google Meet included', 'More speaking time', 'Collaborative learning'] },
      { name: 'Trio Classes (3 students)', description: 'Dynamic, interactive, and affordable', features: ['Small group dynamic', 'More interaction', 'Google Meet included', 'Peer learning', 'Cost-effective'] },
      { name: 'Small Group (4–6 students)', description: 'Best value and real conversation practice', features: ['Best value option', 'Real conversation practice', 'Google Meet included', 'Community atmosphere', 'Consistent schedule'] },
    ],
    offersList: [
      { title: 'Special Offer', description: '20% discount for trio and small group students who purchase 10-class intensive packages.' },
      { title: 'Referral Bonus', description: 'Refer a friend and receive 10% off your next package renewal after they enrol.' },
    ],
  },
  about: {
    whatMakesUsDifferentBadge: 'Our Approach',
    whatMakesUsDifferentTitle: 'What Makes Us Different',
    whatMakesUsDifferentSubtitle: 'Our approach combines expertise with personalization.',
    coreValuesBadge: 'Guiding Principles',
    coreValuesTitle: 'Our Core Values',
    coreValuesSubtitle: 'The principles that guide everything we do.',
    heroImageAlt: 'English pronunciation lesson',
    methodImageAlt: 'Students practising speaking and connecting',
    hero: {
      badge: 'About Us',
      title: 'Our Story',
      body1: 'Frankly Talking English was created with a passion for teaching and a commitment to real communication. With years of experience, certified methodology, and a personalized approach, we help learners speak more clearly, confidently, and naturally.',
      body2: 'We believe that every student deserves high-quality, engaging, and practical English education. Our mission is to break down language barriers and help people connect with the world through confident communication.',
      stat: '500+ students from 30+ countries',
    },
    methodology: {
      badge: 'Our Approach',
      title: 'Our Teaching Methodology',
      body: 'Our lessons are designed to balance structure, communication, and personalization. We believe students learn better when lessons feel practical, engaging, and connected to real life.',
    },
    ctaSection: {
      title: 'Ready to learn with [hl]confidence[/hl]?',
      body: 'Start your journey with a trial lesson or discover your level with our free test.',
      primary: 'Book a Trial Lesson',
      secondary: 'Test Your Level',
    },
    differentiatorsList: [
      { text: 'Personalized learning paths' },
      { text: 'Real communication focus' },
      { text: 'Certified and experienced teaching' },
      { text: 'Flexible learning experience' },
      { text: 'Clear progress support' },
      { text: 'Warm, confidence-building environment' },
    ],
    valuesList: [
      { title: 'Authenticity', description: 'Genuine connections and honest feedback' },
      { title: 'Empathy', description: 'Understanding your unique learning journey' },
      { title: 'Confidence', description: 'Building your ability to communicate freely' },
      { title: 'Excellence', description: 'High standards in everything we do' },
      { title: 'Growth', description: 'Continuous improvement and progress' },
      { title: 'Communication', description: 'Clear, effective, and meaningful exchange' },
    ],
    methodPointsList: [
      { text: 'Communicative approach focused on real-world use' },
      { text: 'Structured lessons with clear learning outcomes' },
      { text: 'Regular feedback and progress tracking' },
      { text: 'Adaptive content based on your interests and goals' },
    ],
  },
  header: {
    toggleMenu: 'Toggle menu',
    closeMenu: 'Close menu',
    login: 'Login',
    signOut: 'Sign Out',
    adminPortal: 'Admin Portal',
    studentPortal: 'Student Portal',
    myPortal: 'My Portal',
    selectLanguage: 'Select language',
  },
  footer: {
    quickLinks: 'Quick Links',
    support: 'Support',
    contact: 'Contact',
    locationDefault: 'ONLINE WORLDWIDE - In-person classes available on request',
    descriptionDefault: 'Personalised online lessons designed to help you communicate clearly, naturally, and confidently.',
    studentAccess: 'Student Access',
    copyright: 'Copyright {{year}} {{siteName}}. All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    taglineSuffix: 'Speak English with Confidence.',
    developerCredit: 'Designed and developed by Suddenly Creative and Technology',
  },
  contact: {
    hero: {
      badge: 'Get in Touch',
      title: "Let's Talk",
      body: "Have questions or want help choosing the right course?\nGet in touch and we'll guide you.",
    },
    ctaCard: {
      title: 'Ready to Start Learning?',
      body: 'Book a trial lesson and experience our teaching approach firsthand.',
      button: 'Book a Trial Lesson',
    },
    phoneText: 'Share your phone or WhatsApp in the form and we will contact you there.',
    locationText: 'Online Worldwide & In-Person Available',
    responseTime: 'We reply within one business day',
    infoEmailTitle: 'Email Us',
    infoPhoneTitle: 'Phone / WhatsApp',
    infoLocationTitle: 'Location',
    infoResponseTimeTitle: 'Response Time',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    phoneLabel: 'Phone / WhatsApp',
    phonePlaceholder: 'Your phone or WhatsApp number',
    inquiryForLabel: 'This inquiry is for',
    inquiryOptionMyself: 'Myself',
    inquiryOptionAnotherPerson: 'Another person',
    inquiryOptionGroup: 'A group of people',
    inquiryOptionCompany: 'A company / team',
    ageLabelSelf: 'Your age',
    ageLabelStudent: 'Student age',
    agePlaceholder: 'Age',
    groupSizeLabel: 'Approximate group size',
    groupSizePlaceholder: 'Number of students',
    companyNameLabel: 'Company name',
    companyNamePlaceholder: 'Company / organization',
    ageRangeLabel: 'Age range of the group',
    ageRangePlaceholder: 'Example: 14-17 or adults',
    teamSizeLabel: 'Team size',
    teamSizePlaceholder: 'Number of participants',
    nativeLanguageLabelTeam: 'Main native language of the team',
    nativeLanguageLabelGroup: 'Main native language of the group',
    nativeLanguageLabelDefault: 'Native language',
    nativeLanguagePlaceholder: 'Portuguese',
    countryLabelCompany: 'Country / company location',
    countryLabelDefault: 'Country',
    countryPlaceholder: 'Brazil',
    messageLabel: 'Message',
    messagePlaceholderCompany: 'Tell us about your team goals, industry, and preferred schedule.',
    messagePlaceholderGroup: 'Tell us about the group, goals, and preferred schedule.',
    messagePlaceholderDefault: 'How can we help you?',
    sendButton: 'Send Message',
    sending: 'Sending...',
    errorFallback: 'Something went wrong. Please try again.',
    successTitle: 'Message Sent!',
    successBody: "Thank you for reaching out. We'll get back to you within one business day.",
    consentPrefix: 'By submitting this form, you confirm that you have read and agree to our',
    consentAnd: 'and',
  },
  bookTrial: {
    durationBadge: '30-45 minutes',
    viaGoogleMeet: 'Via Google Meet',
    scheduleTitle: 'Schedule Your Trial',
    scheduleBodyWithUrl: 'Click the button below to view available times and book your trial lesson. Sessions are approximately 30-45 minutes via Google Meet.',
    scheduleBodyWithoutUrl: 'Our scheduling link is being finalized. Use the button below to contact us directly and we will arrange your trial lesson personally.',
    openCalendarButton: 'Open Scheduling Calendar',
    contactToScheduleButton: 'Contact Us to Schedule',
    scheduleNoteWithUrl: "You'll be redirected to our scheduling page to select your preferred time slot.",
    scheduleNoteWithoutUrl: 'Share your preferred days and times, and our team will reply with the best available options.',
    hero: {
      badge: 'Get Started',
      title: 'Book Your [hl]Trial Lesson[/hl]',
      body: 'Start with a first session designed to understand your level, your goals, and the best path for your learning journey.',
    },
    includesSection: {
      badge: 'Trial Benefits',
      title: 'What Your Trial Includes',
      body: 'A comprehensive introduction to help you start your English journey with confidence.',
    },
    nextStepsSection: {
      badge: 'Next Steps',
      title: 'What Happens After Your Trial',
    },
    bottomCta: {
      body: 'Not ready to book yet? Take our free level test first.',
      button: 'Take Free Level Test',
    },
    includesList: [
      { title: 'Needs Assessment', description: 'We discuss your goals, interests, and what you want to achieve with English.' },
      { title: 'Level Evaluation', description: 'A friendly conversation to understand your current English level.' },
      { title: 'Personalized Plan', description: 'Receive a recommended learning path tailored to your needs.' },
      { title: 'Teaching Style Preview', description: 'Experience our engaging and supportive teaching approach.' },
    ],
    stepsList: [
      { title: 'Receive Your Assessment', description: "You'll get feedback on your current level and areas for improvement." },
      { title: 'Get Your Recommended Plan', description: "We'll suggest the best course and package based on your goals." },
      { title: 'Choose Your Package', description: 'Select the lesson package that fits your schedule and budget.' },
      { title: 'Start Your Journey', description: 'Begin regular lessons and track your progress towards confident English.' },
    ],
  },
  auth: {
    login: {
      title: 'Welcome Back',
      subtitle: 'Sign in to access your learning portal',
      emailLabel: 'Email Address',
      emailPlaceholder: 'your@email.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      submit: 'Sign In',
      submitting: 'Signing in...',
      newHere: 'New here?',
      createAccount: 'Create an Account',
      termsNotice: 'By signing in, you agree to our Terms of Service',
      suspendedMessage: 'Your account is suspended. Please contact support for assistance.',
      toastSuspendedTitle: 'Account suspended',
      toastWelcomeTitle: 'Welcome back!',
      toastWelcomeDescription: 'Logged in as {{name}}',
      toastFailedTitle: 'Login failed',
    },
    register: {
      title: 'Create Account',
      subtitle: 'Join us and start your English journey',
      fullNameLabel: 'Full Name',
      fullNamePlaceholder: 'Your full name',
      emailLabel: 'Email Address',
      emailPlaceholder: 'your@email.com',
      phoneLabel: 'Phone Number',
      phoneOptional: '(optional)',
      phonePlaceholder: '+55 11 99999-9999',
      passwordLabel: 'Password',
      passwordPlaceholder: 'At least 8 characters',
      confirmPasswordLabel: 'Confirm Password',
      confirmPasswordPlaceholder: 'Repeat your password',
      submit: 'Create Account',
      submitting: 'Creating account...',
      alreadyHaveAccount: 'Already have an account?',
      signIn: 'Sign In',
      termsNotice: 'By creating an account, you agree to our Terms of Service',
      errorPasswordMismatch: 'Passwords do not match',
      errorPasswordTooShort: 'Password must be at least 8 characters',
      toastCreatedTitle: 'Account created!',
      toastCreatedDescription: 'Your registration is pending approval.',
      toastFailedTitle: 'Registration failed',
    },
  },
  levelTest: {
    hero: {
      badge: 'Level Assessment',
      title: 'Find Your [hl]English Level[/hl]',
      body: 'Take a quick assessment to understand your current level and receive a recommended learning plan based on your results.',
    },
    emailStep: {
      badge: 'One Last Step',
      title: 'See Your Result and Recommendation',
      body: 'Enter your email to unlock your level assessment, save your result, and receive the recommended plan for your English goals.',
    },
    assessedHeading: 'What will be assessed:',
    categoryGrammar: 'Grammar',
    categoryVocabulary: 'Vocabulary',
    categoryReadingComprehension: 'Reading comprehension',
    categoryPracticalCommunication: 'Practical communication',
    durationNote: 'This test takes approximately 5-10 minutes to complete.',
    startButton: 'Start Level Test',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    saving: 'Saving your result...',
    showResult: 'Show My Result',
    reviewAnswers: 'Review my answers',
    questionCounter: 'Question {{current}} of {{total}}',
    previous: 'Previous',
    next: 'Next',
    continueButton: 'Continue',
    scoreLine: 'Score: {{score}} out of {{total}}',
    recommendedPlanLabel: 'Recommended plan:',
    seePlanOptions: 'See my plan options',
    createAccountCta: 'Create account & get started',
    bookTrialCta: 'Book a Trial Lesson',
    leadErrorFallback: 'Unable to save your result right now. Please try again.',
    categoryBadge: {
      Grammar: 'Grammar',
      Vocabulary: 'Vocabulary',
      Reading: 'Reading',
    },
    results: {
      beginner: {
        title: "You're at a Beginner / Elementary Level",
        description: "You're building your foundation. A structured and supportive learning plan will help you grow in confidence and consistency.",
        plan: 'Starter Plan / General English',
      },
      intermediate: {
        title: "You're at an Intermediate Level",
        description: 'You already have a working foundation. Now is the perfect time to improve fluency, accuracy, and confidence in real communication.',
        plan: 'Intermediate Plan',
      },
      upperIntermediate: {
        title: "You're at an Upper-Intermediate Level",
        description: "You're ready to refine your communication, improve precision, and use English more confidently for real-world and professional situations.",
        plan: 'Professional / Advanced Progress Plan',
      },
      advanced: {
        title: "You're at an Advanced Level",
        description: 'You already communicate with confidence. Now the focus is refinement, precision, sophistication, and advanced fluency.',
        plan: 'Advanced / Intensive Plan',
      },
    },
  },
  legal: {
    badge: 'Legal',
    privacy: {
      title: 'Privacy Policy',
      intro: 'We collect only the information needed to respond to inquiries, manage student accounts, and provide classes safely and effectively.',
      collectTitle: 'Information We Collect',
      collectBody: 'We may collect your name, email address, phone number, course preferences, contact form messages, trial requests, account details, and learning activity submitted through the student portal.',
      useTitle: 'How We Use Your Information',
      useBody: 'We use your information to answer inquiries, organize trial lessons, recommend courses, manage student access, communicate about classes, and improve the learning experience. We may also use aggregate website data to understand performance and marketing effectiveness.',
      sharingTitle: 'Data Sharing',
      sharingBody: 'We do not sell your personal information. We may share limited data with trusted service providers such as scheduling, analytics, payment, or hosting platforms only when needed to operate the school and deliver our services.',
      retentionTitle: 'Retention and Security',
      retentionBody: 'We retain information only as long as necessary for legitimate business, educational, legal, and support purposes. We use reasonable administrative and technical safeguards to protect the data stored in our systems.',
      rightsTitle: 'Your Rights',
      rightsBody: 'You can request access, correction, or deletion of the personal information we store about you, subject to legal and operational requirements. To make a request, contact us at',
      updatesTitle: 'Updates',
      updatesBody: 'We may update this policy as the school, legal requirements, or operating tools evolve. Continued use of the site after an update means the revised policy applies.',
    },
    terms: {
      title: 'Terms of Service',
      intro: 'These terms govern the use of our website, trial booking flow, contact forms, and student portal.',
      servicesTitle: 'Services',
      servicesBody: 'Frankly Talking English provides English teaching services, trial lessons, placement guidance, and student learning tools. Course formats, schedules, packages, and availability may change according to teacher capacity and program needs.',
      bookingTitle: 'Booking and Communication',
      bookingBody: 'Trial requests, contact submissions, and student registrations must contain accurate information. We may contact you by email or other approved channels to confirm availability, placement, scheduling, and operational updates.',
      accountsTitle: 'Student Accounts',
      accountsBody: 'Portal access is personal and must not be shared. Users are responsible for maintaining the confidentiality of their login credentials and for activities performed through their account.',
      paymentsTitle: 'Payments and Scheduling',
      paymentsBody: 'Prices, payment methods, package terms, and lesson schedules are confirmed during the enrollment process. Rescheduling, cancellation, and refund conditions may vary by package and will be communicated before the service begins.',
      acceptableUseTitle: 'Acceptable Use',
      acceptableUseBody: 'Users may not misuse the site, interfere with platform security, attempt unauthorized access, or submit unlawful, abusive, or misleading content through any form, class, message, or portal feature.',
      contactTitle: 'Contact',
      contactBody: 'If you have questions about these terms, contact us at',
    },
  },
  testimonials: {
    readFullStory: 'Read full story',
    hero: {
      badge: 'Testimonials',
      title: 'Real stories from real students',
      body: "Discover how students from different backgrounds improved their English confidence with Cida's supportive and personalized teaching approach.",
    },
    ctaSection: {
      title: 'Ready to Start Your Own [hl]Success Story[/hl]?',
      body: 'Start with a free level test or book a trial lesson and find the right path for your goals.',
      primary: 'Test Your Level',
      secondary: 'Book a Trial',
    },
  },
  testimonialDetail: {
    backLink: 'Back to testimonials',
    badge: 'Student Story',
    ctaTitle: 'Ready to build your confidence in English?',
    ctaBody: 'Start your journey with personalized English classes designed around your goals.',
    ctaPrimary: 'Start your journey today',
    ctaSecondary: 'Book a Trial',
  },
  faq: {
    hero: {
      badge: 'Support',
      title: 'Frequently Asked Questions',
      body: 'Find answers to common questions about our courses, scheduling, and more.',
    },
    ctaSection: {
      title: 'Still have questions?',
      body: "We're here to help. Get in touch and we'll answer any questions you have.",
      primary: 'Contact Us',
      secondary: 'Book a Trial',
    },
    faqList: [
      { question: 'How do I book classes?', answer: 'Start with the free level test or a trial lesson. After we understand your goals and current level, we confirm the best package and available schedule with you directly.' },
      { question: 'Do you offer online lessons?', answer: 'Yes. Online lessons are delivered live through Google Meet. All you need is a stable internet connection and a computer or tablet with a camera and microphone.' },
      { question: 'Do you offer in-person lessons?', answer: 'Yes, depending on availability and location. Contact us to discuss in-person lesson options in your area.' },
      { question: 'How does the level test work?', answer: 'Our level test is a quick online assessment that evaluates your grammar, vocabulary, reading, and listening skills. It takes about 5-10 minutes and gives you a practical understanding of your current English level, helping us recommend the best learning plan for you.' },
      { question: 'How do I choose the right package?', answer: "We recommend taking the free level test first, which will help us understand your current level and suggest an appropriate package. You can also book a trial lesson where we can discuss your goals and recommend the best option for you." },
      { question: 'Can I reschedule lessons?', answer: 'Yes. Lessons can be rescheduled when requested through the booking agenda at least 24 hours in advance. If the student does not notify us with 24 hours’ notice, the missed lesson will not be eligible for a refund or replacement class.' },
      { question: 'Do you teach children and teens?', answer: 'We currently teach students aged 16 and above. At the moment, we do not offer classes for younger children.' },
      { question: 'Do you provide Business English?', answer: 'Yes. We offer Business English classes focused on real professional communication, including meetings, presentations, emails, negotiations, and interviews. These classes can be tailored to your specific industry or profession.' },
      { question: 'What qualifications do your teachers have?', answer: 'Our teacher is CELTA (Cambridge) certified, holds a Master’s degree in TESOL, and is currently pursuing a Doctorate focused on teaching English in the AI era, combining internationally recognized credentials with cutting-edge research in language education.' },
      { question: 'How long does it take to see results?', answer: 'Progress varies depending on your starting level, goals, and how much time you dedicate to learning. Most students notice improvements within the first few weeks, particularly in confidence and fluency.' },
      { question: 'What materials do you use?', answer: 'We use a combination of high-quality course materials, custom resources, and real-world content tailored to your interests and goals. All materials are provided through Google Classroom.' },
      { question: 'How do I pay for lessons?', answer: 'Payment options are confirmed during enrollment based on the selected package and location. Contact us and we will share the available payment methods and next steps.' },
    ],
  },
};

export default en;
